class HimalayanRealm extends HTMLElement {
  #controller;
  #observer;
  #revealObserver;
  #frame = 0;
  #scrollFrame = 0;
  #hero;
  #veil;
  #reduce;
  #hover;
  #target = { x: 0, y: 0, radius: 0 };
  #current = { x: 0, y: 0, radius: 0 };
  #seeded = false;
  #last = { x: 0, y: 0, distance: 0 };

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#hero = this.querySelector('.rhero');
    this.#veil = this.querySelector('.rhero__veil');
    this.#reduce = matchMedia('(prefers-reduced-motion: reduce)');
    this.#hover = matchMedia('(hover: hover)');
    this.#hero.addEventListener('pointermove', this.#point, { passive: true, signal });
    this.#hero.addEventListener('pointerleave', this.#leave, { signal });
    document.addEventListener('scroll', this.#scheduleScroll, { capture: true, passive: true, signal });
    window.addEventListener('resize', this.#scheduleScroll, { passive: true, signal });
    this.#reduce.addEventListener('change', this.#reset, { signal });
    this.#reduce.addEventListener('change', this.#reveal, { signal });
    this.#hover.addEventListener('change', this.#reset, { signal });
    this.#observer = new IntersectionObserver(entries => {
      for (const entry of entries) if (entry.isIntersecting) {
        entry.target.classList.add('is-lit');
        this.#observer.unobserve(entry.target);
      }
    }, { rootMargin: '0px 0px -22% 0px', threshold: 0.02 });
    this.querySelectorAll('.rsec').forEach(section => this.#observer.observe(section));
    this.#reveal();
    this.addEventListener('shopify:block:select', this.#showSelected, { signal });
    this.addEventListener('focusin', this.#showSelected, { signal });
    this.#scroll();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    this.#revealObserver?.disconnect();
    this.classList.remove('is-reveal-ready');
    this.#reset();
  }

  #reveal = () => {
    this.#revealObserver?.disconnect();
    const elements = this.querySelectorAll('.rsec .rlabel, .rlead, .rbody, .rplate, .rart__b, .rstrip, .rnote, .rfig, .rcard, .rnine, .rcta');
    elements.forEach(element => element.classList.add('rev'));
    this.querySelectorAll('.rlead, .rnine').forEach(element => element.dataset.d = '1');
    this.querySelectorAll('.rbody, .rplate, .rnote').forEach(element => element.dataset.d = '2');
    const heroElements = this.querySelectorAll('.rhero__num, .rhero h1, .rhero__zh, .rhero__line');
    heroElements.forEach(element => element.classList.add('rev', 'is-in'));
    this.querySelectorAll('.rhero h1, .rhero__zh').forEach(element => element.dataset.d = '1');
    this.querySelector('.rhero__line').dataset.d = '2';
    this.querySelectorAll('.rfig').forEach((element, index) => element.dataset.d = index % 3);
    this.querySelectorAll('.rstrip__g, .rgrid').forEach(grid => {
      grid.querySelectorAll('.rcard').forEach((element, index) => element.dataset.d = index % 3);
    });
    if (this.#reduce.matches || window.Shopify?.designMode) {
      elements.forEach(element => element.classList.add('is-in'));
      this.querySelectorAll('.rsec').forEach(section => section.classList.add('is-lit'));
      this.classList.remove('is-reveal-ready');
      return;
    }
    this.#revealObserver = new IntersectionObserver(entries => {
      for (const entry of entries) if (entry.isIntersecting) {
        entry.target.classList.add('is-in');
        this.#revealObserver.unobserve(entry.target);
      }
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.08 });
    elements.forEach(element => {
      if (!element.classList.contains('is-in')) this.#revealObserver.observe(element);
    });
    this.classList.add('is-reveal-ready');
  };

  #showSelected = event => {
    const selected = event.target;
    if (!(selected instanceof Element)) return;
    let ancestor = selected;
    while (ancestor && ancestor !== this) {
      if (ancestor.classList.contains('rev')) ancestor.classList.add('is-in');
      ancestor = ancestor.parentElement;
    }
    selected.querySelectorAll('.rev').forEach(element => element.classList.add('is-in'));
  };

  #scheduleScroll = () => {
    if (!this.#scrollFrame) this.#scrollFrame = requestAnimationFrame(this.#scroll);
  };

  #scroll = () => {
    this.#scrollFrame = 0;
    const y = Math.max(0, -this.getBoundingClientRect().top);
    if (this.#reduce.matches) return;
    const wrapper = this.closest('.page-wrapper');
    const scroller = wrapper && ['auto', 'scroll'].includes(getComputedStyle(wrapper).overflowY) ? wrapper : document.scrollingElement;
    const run = Math.max(1, scroller.scrollHeight - scroller.clientHeight);
    this.querySelector('.rbeam').style.setProperty('--beam', `${Math.min(1, Math.max(0, scroller.scrollTop) / run) * 128}vh`);
    if (y > innerHeight * 1.2) return;
    this.querySelector('.rhero__moon').style.transform = `translate3d(0,${y * 0.26}px,0)`;
    this.#hero.style.setProperty('--py', `${y * 0.13}px`);
  };

  #point = event => {
    if (this.#reduce.matches || !this.#hover.matches || event.pointerType !== 'mouse') return;
    const box = this.#veil.getBoundingClientRect();
    this.#target = { x: event.clientX - box.left, y: event.clientY - box.top, radius: box.width * 0.42 };
    if (!this.#seeded) {
      this.#current.x = this.#target.x;
      this.#current.y = this.#target.y;
      this.#seeded = true;
    }
    const hero = this.#hero.getBoundingClientRect();
    const x = event.clientX - hero.left;
    const y = event.clientY - hero.top;
    this.#hero.style.setProperty('--gx', `${x}px`);
    this.#hero.style.setProperty('--gy', `${y}px`);
    this.#hero.classList.add('is-touched', 'is-glinting');
    this.#last.distance += Math.abs(x - this.#last.x) + Math.abs(y - this.#last.y);
    this.#last.x = x;
    this.#last.y = y;
    if (this.#last.distance > 120) {
      this.#last.distance = 0;
      this.#spark(x, y);
    }
    this.#kick();
  };

  #spark(x, y) {
    if (this.#hero.querySelectorAll('.rspark').length >= 14) return;
    const spark = document.createElement('i');
    spark.className = 'rspark';
    spark.setAttribute('aria-hidden', 'true');
    spark.style.left = `${x + Math.random() * 46 - 23}px`;
    spark.style.top = `${y + Math.random() * 46 - 23}px`;
    const angle = Math.random() * Math.PI * 2;
    spark.style.setProperty('--s', `${11 + Math.random() * 12}px`);
    spark.style.setProperty('--t', `${820 + Math.random() * 700}ms`);
    spark.style.setProperty('--dx', `${Math.cos(angle) * (12 + Math.random() * 26)}px`);
    spark.style.setProperty('--dy', `${Math.sin(angle) * (10 + Math.random() * 22) - 14}px`);
    spark.addEventListener('animationend', () => spark.remove(), { once: true });
    this.#hero.append(spark);
  }

  #kick = () => {
    if (!this.#frame) this.#frame = requestAnimationFrame(this.#paint);
  };

  #paint = () => {
    this.#frame = 0;
    this.#current.x += (this.#target.x - this.#current.x) * 0.16;
    this.#current.y += (this.#target.y - this.#current.y) * 0.16;
    this.#current.radius += (this.#target.radius - this.#current.radius) * 0.11;
    this.#veil.style.setProperty('--mx', `${this.#current.x.toFixed(1)}px`);
    this.#veil.style.setProperty('--my', `${this.#current.y.toFixed(1)}px`);
    this.#veil.style.setProperty('--r', `${this.#current.radius.toFixed(1)}px`);
    if (Object.keys(this.#target).some(key => Math.abs(this.#target[key] - this.#current[key]) > 0.4)) this.#kick();
  };

  #leave = () => {
    this.#target.radius = 0;
    this.#last.distance = 0;
    this.#hero.classList.remove('is-glinting');
    this.#kick();
  };

  #reset = () => {
    cancelAnimationFrame(this.#frame);
    cancelAnimationFrame(this.#scrollFrame);
    this.#frame = this.#scrollFrame = 0;
    this.#target = { x: 0, y: 0, radius: 0 };
    this.#current = { ...this.#target };
    this.#seeded = false;
    this.#veil.style.removeProperty('--r');
    this.#hero.classList.remove('is-glinting', 'is-touched');
    this.#hero.style.removeProperty('--py');
    this.querySelector('.rhero__moon').style.removeProperty('transform');
    this.querySelectorAll('.rspark').forEach(spark => spark.remove());
  };
}

if (!customElements.get('himalayan-realm')) customElements.define('himalayan-realm', HimalayanRealm);
